// Single writer per deck (TODO §12.5).
//
// Two pages with the same deck open used to clobber each other silently: every document
// action rewrites the whole `decks` record, so last write won and the losing page kept
// showing state that no longer existed on disk. The honest cheap answer is not to let that
// happen — live multi-writer editing needs a CRDT and is out of scope (§12.5.2).
//
// So: on open, a page takes an exclusive Web Lock named `deck:<id>` and holds it for as
// long as it has the deck open. Whoever holds the lock is the writer. A second page opens
// **read-only** with a banner and an "Edit here" take-over, which asks the holder to let go
// over the BroadcastChannel; the demoted page re-hydrates as a viewer.
//
// `canWriteDeck` is what the persistence listener consults — the gate is here, not in
// `idb.ts`, because storage-level operations (create, duplicate, import, delete) are issued
// from the documents home where no session exists and must stay allowed.
//
// Web Locks is missing in insecure contexts and some webviews. That degrades to "no lock,
// editing allowed" (§12.5.5) rather than blocking the user out of their own deck — the
// `meta.rev` check in `putDeck` is the backstop there.

import {
  PAGE_ID,
  postDeckMessage,
  subscribeDeckMessages,
} from "@/lib/deckChannel";

export interface DeckSessionState {
  deckId: string | null;
  // Does this page own the deck? Meaningless until `settled`.
  writable: boolean;
  // False while the lock is still being acquired. The editor must not paint — let alone
  // write — before we know which side of the fence we are on.
  settled: boolean;
  // Running without Web Locks (§12.5.5): editing is allowed, but nothing stops a second
  // page from doing the same. `putDeck`'s rev check catches the collision after the fact.
  degraded: boolean;
}

const IDLE: DeckSessionState = {
  deckId: null,
  writable: false,
  settled: false,
  degraded: false,
};

// A same-page release is not instantaneous — the browser hands the lock back only after our
// hold callback's promise settles. React StrictMode remounts (dev) and take-over hand-offs
// both re-request within that window, so a failed grab is retried briefly before we conclude
// someone else really owns the deck.
const ACQUIRE_ATTEMPTS = 4;
const ACQUIRE_RETRY_MS = 75;
// How long a take-over waits for the current holder to answer before giving up.
const HANDOFF_TIMEOUT_MS = 2000;

let state: DeckSessionState = IDLE;
const listeners = new Set<(s: DeckSessionState) => void>();

// Bumped by every start and every end. Acquiring a lock is asynchronous, so by the time an
// answer comes back the session it belongs to may already be over — a React StrictMode
// remount and a fast deck switch both do exactly that. Comparing generations (rather than
// deck ids) catches the case the ids cannot: two successive sessions for the SAME deck,
// where a stale "I got the lock" would otherwise mark a session ready that has since
// released it.
let generation = 0;

// A granted Web Lock, with the one operation that gives it back. Each grant carries its
// OWN release rather than writing to a shared module variable: a later grab used to
// overwrite that variable, orphaning the earlier hold with no way left to release it — and
// an orphaned hold locks the page out of its own deck for good.
interface Hold {
  release: () => void;
}

// The hold backing `state.writable`, or null when this page is a viewer. Only ever assigned
// together with `writable`, so the two cannot disagree.
let currentHold: Hold | null = null;

// Session starts are serialized through this. Acquiring is asynchronous, so without a queue
// a replacement session races the one it replaces — and in React StrictMode (which mounts,
// unmounts and remounts every effect in dev) that race is guaranteed, not occasional.
let queue: Promise<unknown> = Promise.resolve();

let unsubscribe: (() => void) | null = null;
// Set while a take-over is waiting for the holder's `lock-released`.
let handoffWaiter: (() => void) | null = null;

function lockName(deckId: string): string {
  return `deck:${deckId}`;
}

function setState(next: Partial<DeckSessionState>): void {
  state = { ...state, ...next };
  for (const fn of [...listeners]) fn(state);
}

export function getDeckSession(): DeckSessionState {
  return state;
}

export function subscribeDeckSession(
  fn: (s: DeckSessionState) => void,
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// The persistence listener's gate. A page that does not hold the lock writes nothing —
// its edits stay in memory and the banner says so.
export function canWriteDeck(deckId: string): boolean {
  return state.settled && state.writable && state.deckId === deckId;
}

const delay = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

// Nothing was actually locked (the request itself failed), but the caller is treated as the
// writer anyway — see the catch below. Releasing it is a no-op because there is nothing to
// give back.
const NO_LOCK: Hold = { release: () => {} };

// Take the lock and hold it. Resolves a `Hold` when we own it, null when another page does.
// The lock stays held by the unresolved promise the callback returns; `Hold.release`
// resolves it, which is what hands the lock back to the browser.
//
// **Whoever receives a Hold owns it.** If the session it was taken for has since ended, the
// receiver must release it — dropping it on the floor leaks the lock for the page's lifetime.
function grabLock(deckId: string): Promise<Hold | null> {
  return new Promise<Hold | null>((settle) => {
    let answered = false;
    const answer = (hold: Hold | null) => {
      if (answered) return;
      answered = true;
      settle(hold);
    };
    navigator.locks
      .request(lockName(deckId), { ifAvailable: true }, (lock) => {
        if (!lock) {
          answer(null);
          return;
        }
        return new Promise<void>((done) => {
          let released = false;
          answer({
            release: () => {
              if (released) return;
              released = true;
              done();
            },
          });
        });
      })
      .catch((err) => {
        // A rejected request tells us nothing about who owns the deck, so fail open —
        // locking the user out of their own document on an API hiccup is the worse bug.
        console.warn("[deck-session] lock request failed", err);
        answer(NO_LOCK);
      });
  });
}

async function acquire(deckId: string): Promise<Hold | null> {
  for (let attempt = 0; attempt < ACQUIRE_ATTEMPTS; attempt++) {
    const hold = await grabLock(deckId);
    if (hold) return hold;
    if (attempt < ACQUIRE_ATTEMPTS - 1) await delay(ACQUIRE_RETRY_MS);
  }
  return null;
}

// Install a freshly-acquired hold, or give it straight back when the session it was taken
// for is already over. Every acquisition in this module ends here, which is what guarantees
// no grant is ever dropped without being released.
function adoptHold(hold: Hold | null, gen: number): boolean {
  if (gen !== generation) {
    hold?.release();
    return false;
  }
  if (currentHold && currentHold !== hold) currentHold.release();
  currentHold = hold;
  return hold !== null;
}

// Give up the lock (if we hold it) and tell whoever asked. `to` is the requesting page, or
// "*" when we are going away entirely and anyone may take it.
function release(to: string): void {
  const deckId = state.deckId;
  currentHold?.release();
  currentHold = null;
  if (deckId) postDeckMessage({ type: "lock-released", deckId, to });
}

function handleMessage(msg: {
  type: string;
  deckId?: string;
  from?: string;
  to?: string;
}): void {
  if (!state.deckId || msg.deckId !== state.deckId) return;

  // Another page wants to edit the deck we hold. Step down: release, go read-only, and
  // re-hydration follows from the demotion (the viewer path re-reads on `deck-updated`).
  if (msg.type === "lock-request" && state.writable && !state.degraded) {
    release(msg.from ?? "*");
    setState({ writable: false });
    return;
  }

  if (msg.type === "lock-released") {
    // Our take-over's answer.
    if (msg.to === PAGE_ID) {
      handoffWaiter?.();
      return;
    }
    // The writer closed its page. Nobody owns the deck now, so a viewer may quietly
    // promote itself — there is no other page whose editing this could interrupt.
    if (msg.to === "*" && state.settled && !state.writable) {
      const gen = generation;
      void acquire(state.deckId).then((hold) => {
        if (adoptHold(hold, gen)) setState({ writable: true });
      });
    }
  }
}

// Open a session for `deckId`, replacing any previous one. Resolves once the outcome is
// known, so the caller can hold the editor back until then.
export function startDeckSession(deckId: string): Promise<DeckSessionState> {
  // Queued, not fired: a start must not begin acquiring until the previous session has
  // finished disposing of its own grant, or the two fight over the same lock and both lose.
  const run = queue.then(
    () => beginSession(deckId),
    () => beginSession(deckId),
  );
  queue = run.catch(() => {});
  return run;
}

async function beginSession(deckId: string): Promise<DeckSessionState> {
  endDeckSession();
  const gen = ++generation;

  // No Web Locks here (insecure context, some webviews): run degraded (§12.5.5).
  if (typeof navigator === "undefined" || !navigator.locks) {
    setState({ deckId, writable: true, settled: true, degraded: true });
    return state;
  }

  setState({ deckId, writable: false, settled: false, degraded: false });
  unsubscribe = subscribeDeckMessages(handleMessage);
  const owned = adoptHold(await acquire(deckId), gen);
  // A newer session may have started while we were waiting; don't stomp it.
  if (gen !== generation) return state;
  setState({ writable: owned, settled: true });
  return state;
}

export function endDeckSession(): void {
  generation++;
  if (state.deckId && state.writable) release("*");
  // Belt and braces: release even when we never got as far as being marked writable. This
  // is the path StrictMode's mount→cleanup→mount takes, where the grant is still in flight
  // (and is handed back by `adoptHold` when it lands).
  currentHold?.release();
  currentHold = null;
  unsubscribe?.();
  unsubscribe = null;
  handoffWaiter = null;
  state = IDLE;
  for (const fn of [...listeners]) fn(state);
}

// §12.5.2 "Edit here" from a read-only page: ask the holder to release, then take the lock.
// Resolves true once this page is the writer — the caller then re-hydrates, because the
// deck has almost certainly moved on since this page last read it.
export async function takeOverDeck(): Promise<boolean> {
  const deckId = state.deckId;
  if (!deckId) return false;
  if (state.writable) return true;
  // Pinned before the hand-off wait, not after: the user can navigate away mid-take-over,
  // and a lock grabbed for a session that no longer exists must not report success.
  const gen = generation;

  // Fast path first. The holder is often simply gone — its window was closed, or it never
  // existed and this page only *thought* it was locked out. Asking a window that isn't
  // there costs the full hand-off timeout and then fails, which reads as "stuck" rather
  // than "someone else has it", so try the lock before asking anyone to stand down.
  if (adoptHold(await acquire(deckId), gen)) {
    setState({ writable: true, settled: true });
    return true;
  }
  if (gen !== generation) return false;

  postDeckMessage({ type: "lock-request", deckId, from: PAGE_ID });

  // Wait for the holder's answer, but don't hang on it: the holder may have died without
  // releasing, in which case the lock is already free and the acquire below just works.
  await new Promise<void>((resolve) => {
    const timer = setTimeout(finish, HANDOFF_TIMEOUT_MS);
    function finish() {
      clearTimeout(timer);
      handoffWaiter = null;
      resolve();
    }
    handoffWaiter = finish;
  });

  if (gen !== generation) return false;
  const owned = adoptHold(await acquire(deckId), gen);
  if (gen !== generation) return false;
  setState({ writable: owned, settled: true });
  return owned;
}

// Is some other page holding this deck's lock? Used from the documents home (which has no
// session of its own) to keep a rename from racing the window that is actually editing —
// there, a write would bump `rev` out from under the writer and cost it its next save.
// Where Web Locks is unavailable this answers false, which matches the degraded rule: we
// cannot know, so we do not block (§12.5.5).
export async function isDeckOpenElsewhere(deckId: string): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.locks) return false;
  try {
    const held = await navigator.locks.request(
      lockName(deckId),
      { ifAvailable: true },
      // Granted means nobody else has it — and we let go immediately by returning.
      (lock) => lock !== null,
    );
    return !held;
  } catch (err) {
    console.warn("[deck-session] lock probe failed", err);
    return false;
  }
}

// The browser releases a Web Lock when the page goes away, but it does not tell the other
// pages. Announcing it on the way out lets a waiting viewer promote itself immediately
// instead of sitting behind a stale "open in another window" banner.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (state.deckId && state.writable) release("*");
  });
}
